import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Icons } from "../components/ui/icons";
import SearchInput from "../components/search-input";
import DataTable, { TableRowData } from "../components/data-table";
import StatisticsCard from "../components/statistics-card";
import PageShell, { StatItem } from "../components/page-shell";
import { DesktopOnly } from "../components/responsive";
import { useIsMobile } from "../hooks/use-is-mobile";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import OverdueSiteRemarksDrawer from "../components/overdue-site-remarks-drawer";
import { overdueSiteRemindersTableColumns } from "../lib/table-columns";
import { OverdueDeliverySite, getOverdueDeliverySites } from "../lib/api";

const formatOverdueSince = (value: string) => {
  if (!value || value === "—") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return value;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return format(date, "MMM dd, yyyy");
};

const formatSiteCustomer = (site: OverdueDeliverySite) => {
  const customer = site.customer?.fullName ?? "—";
  const phone = site.customer?.phoneNumber ?? "—";
  return `${site.name}\n${customer}\n${phone}`;
};

const formatLatestRemark = (site: OverdueDeliverySite) => {
  const remarks = site.remarks ?? [];
  if (remarks.length === 0) return "—";
  const latest = remarks[remarks.length - 1];
  const preview =
    latest.text.length > 80 ? `${latest.text.slice(0, 80)}…` : latest.text;
  return `${remarks.length} · ${preview}`;
};

const Reminders: React.FC = () => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const overdueSitesQuery = useQuery({
    queryKey: ["sites", "overdue"],
    queryFn: async () => getOverdueDeliverySites(),
  });

  const filteredSites = useMemo(() => {
    const sites = overdueSitesQuery.data ?? [];
    if (!searchTerm.trim()) return sites;
    const term = searchTerm.trim().toLowerCase();
    return sites.filter((site) => {
      const haystack = [
        site.name,
        site.customer?.fullName,
        site.customer?.phoneNumber,
        site.region,
        site.district,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [overdueSitesQuery.data, searchTerm]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSites.slice(start, start + pageSize);
  }, [filteredSites, currentPage, pageSize]);

  const rows = useMemo(
    () =>
      paginatedRows.map((site) => ({
        id: site.id,
        site_customer: formatSiteCustomer(site),
        overdue_since: formatOverdueSince(site.overdueSince),
        remark_count: formatLatestRemark(site),
        raw_data: site,
      })) as TableRowData[],
    [paginatedRows],
  );

  const totalItems = filteredSites.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const sitesWithRemarks = filteredSites.filter(
    (site) => (site.remarks?.length ?? 0) > 0,
  ).length;

  const actions = [
    {
      label: "View / add remarks",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => setSelectedSiteId(row.id as string),
    },
  ];

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["sites", "overdue"] });

  const stats: StatItem[] = [
    {
      label: "Overdue sites",
      value: String(filteredSites.length),
      icon: <Icons.Bell className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "With remarks",
      value: String(sitesWithRemarks),
      icon: <Icons.Document className="w-5 h-5 text-amber-600" />,
    },
    {
      label: "Without remarks",
      value: String(Math.max(0, filteredSites.length - sitesWithRemarks)),
      icon: <Icons.Document className="w-5 h-5 text-gray-600" />,
    },
  ];

  return (
    <>
      <PageShell
        title="Reminders"
        description="Follow up on overdue delivery sites and add remarks. Remarks are included in the daily overdue sites email at 12:00 PM."
        loading={overdueSitesQuery.isLoading}
        toolbar={
          <RefreshToolbarButton
            isRefreshing={overdueSitesQuery.isFetching}
            onRefresh={refresh}
          />
        }
        stats={isMobile ? stats : undefined}
        mobile={{ onRefresh: refresh }}
      >
        <DesktopOnly>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.map((item) => (
              <StatisticsCard
                key={item.label}
                label={item.label}
                value={item.value}
                icon={item.icon}
              />
            ))}
          </div>
        </DesktopOnly>

        <SearchInput
          searchTerm={searchInput}
          onSearchChange={setSearchInput}
          placeholder="Search site, customer, phone..."
          className="w-full md:max-w-sm"
        />

        <DataTable
          columns={overdueSiteRemindersTableColumns}
          data={rows}
          actions={actions}
          loading={overdueSitesQuery.isFetching && !overdueSitesQuery.isLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </PageShell>

      <OverdueSiteRemarksDrawer
        siteId={selectedSiteId}
        onClose={() => setSelectedSiteId(null)}
      />
    </>
  );
};

export default Reminders;
